import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { coreAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import './Admin.css';

const emptyForm = {
  name: '',
  position: 'member',
  position_title: '',
  description: '',
  email: '',
  phone: '',
  facebook_url: '',
  instagram_url: '',
  display_order: 0,
  is_active: true,
};

const OrganizationMemberForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isEdit) {
      fetchMember();
    }
  }, [id]);

  const fetchMember = async () => {
    try {
      setLoading(true);
      const response = await coreAPI.adminGetOrganizationMember(id);
      const { photo, organization, ...rest } = response.data;
      setForm({ ...emptyForm, ...rest });
      setPhotoUrl(photo || null);
    } catch (err) {
      error('無法載入成員資料');
      navigate('/admin/organization-members');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      error('請選擇圖片文件');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      error('圖片大小不能超過 5MB');
      return;
    }

    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handlePhotoRemove = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const buildPayload = () => {
    const { photo, organization, ...fields } = form;
    const data = {
      ...fields,
      display_order: Number(form.display_order) || 0,
    };

    if (photoFile) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      formData.append('photo', photoFile);
      return formData;
    }

    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await coreAPI.adminUpdateOrganizationMember(id, payload);
        success('成員已更新');
      } else {
        await coreAPI.adminCreateOrganizationMember(payload);
        success('成員已建立');
      }
      navigate('/admin/organization-members');
    } catch (err) {
      error(isEdit ? '更新失敗' : '建立失敗');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const currentPhoto = photoPreview || photoUrl;

  if (loading) return <LoadingPage message="載入成員資料中..." />;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>{isEdit ? '編輯組織成員' : '新增組織成員'}</h1>
      </div>

      <form className="admin-form card" onSubmit={handleSubmit}>
        <div className="form-group photo-upload-group">
          <label>頭像</label>
          <div className="photo-upload-section">
            <div className="photo-preview-container">
              {currentPhoto ? (
                <img src={currentPhoto} alt={form.name || '成員頭像'} className="photo-preview" />
              ) : (
                <div className="photo-preview photo-preview-placeholder">
                  {form.name ? form.name.charAt(0) : '?'}
                </div>
              )}
            </div>
            <div className="photo-upload-controls">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="photo-file-input"
              />
              {photoFile && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={handlePhotoRemove}>
                  取消選擇
                </button>
              )}
              <p className="form-hint">支援 JPG、PNG、GIF、WebP，最大 5MB</p>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>姓名 *</label>
            <input name="name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>職位</label>
            <select name="position" value={form.position} onChange={handleChange}>
              <option value="chairman">主席</option>
              <option value="vice_chairman">副主席</option>
              <option value="secretary">秘書</option>
              <option value="treasurer">司庫</option>
              <option value="director">理事</option>
              <option value="advisor">顧問</option>
              <option value="member">委員</option>
              <option value="other">其他</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>自訂職位名稱 (留空使用預設)</label>
          <input name="position_title" value={form.position_title || ''} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>簡介 (支援 HTML)</label>
          <textarea name="description" value={form.description || ''} onChange={handleChange} rows={5} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>電郵</label>
            <input name="email" type="email" value={form.email || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>電話</label>
            <input name="phone" value={form.phone || ''} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Facebook 連結</label>
            <input name="facebook_url" value={form.facebook_url || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Instagram 連結</label>
            <input name="instagram_url" value={form.instagram_url || ''} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>顯示順序</label>
            <input name="display_order" type="number" value={form.display_order} onChange={handleChange} />
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input name="is_active" type="checkbox" checked={form.is_active} onChange={handleChange} />
              啟用
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/organization-members')}>
            取消
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '儲存中...' : '儲存'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrganizationMemberForm;
